import { useState, useCallback } from 'react';
import { get, patch } from 'aws-amplify/api';

// Default time slots for restaurant (11 AM to 10 PM in 15-minute intervals)
export const DEMAND_HOURS = [
  '11:00', '11:15', '11:30', '11:45',
  '12:00', '12:15', '12:30', '12:45',
  '13:00', '13:15', '13:30', '13:45',
  '14:00', '14:15', '14:30', '14:45',
  '15:00', '15:15', '15:30', '15:45',
  '16:00', '16:15', '16:30', '16:45',
  '17:00', '17:15', '17:30', '17:45',
  '18:00', '18:15', '18:30', '18:45',
  '19:00', '19:15', '19:30', '19:45',
  '20:00', '20:15', '20:30', '20:45',
  '21:00', '21:15', '21:30', '21:45'
];

export const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const getMonday = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(d.setDate(diff));
};

export const useDemandPlanning = (getAuthToken, setLoading, setError, setSuccess) => {
  const [defaultDemandData, setDefaultDemandData] = useState({});
  const [weeklyDemandOverrides, setWeeklyDemandOverrides] = useState({});
  const [onlineOrderDemand, setOnlineOrderDemand] = useState({});
  const [onlineOrderWeeklyOverrides, setOnlineOrderWeeklyOverrides] = useState({});
  const [maxCapacity, setMaxCapacity] = useState(100);
  const [demandSelectedDay, setDemandSelectedDay] = useState('monday');
  const [demandHasChanges, setDemandHasChanges] = useState(false);
  const [avgLengthOfStay, setAvgLengthOfStay] = useState(90);
  const [demandViewMode, setDemandViewMode] = useState('template');
  const [demandWeekStart, setDemandWeekStart] = useState(() => {
    const monday = getMonday(new Date());
    return monday.toISOString().split('T')[0];
  });

  const initializeDemandData = () => {
    const initialData = {};
    DAY_NAMES.forEach(day => {
      initialData[day] = {};
      DEMAND_HOURS.forEach(hour => {
        initialData[day][hour] = 0;
      });
    });
    setDefaultDemandData(initialData);
  };

  const fetchDemandData = useCallback(async () => {
    try {
      const token = await getAuthToken();
      const response = await get({
        apiName: 'ChulasEmployeeAPI',
        path: '/v1/admin/scheduling/demand',
        options: { headers: { Authorization: token } }
      }).response;
      const data = await response.body.json();
      if (data.default_demand) {
        setDefaultDemandData(data.default_demand);
      } else if (data.demand) {
        setDefaultDemandData(data.demand);
      }
      if (data.weekly_overrides) {
        setWeeklyDemandOverrides(data.weekly_overrides);
      }
      if (data.online_order_demand) {
        setOnlineOrderDemand(data.online_order_demand);
      }
      if (data.online_order_weekly_overrides) {
        setOnlineOrderWeeklyOverrides(data.online_order_weekly_overrides);
      }
      if (data.max_capacity) {
        setMaxCapacity(data.max_capacity);
      }
      if (data.avg_length_of_stay) {
        setAvgLengthOfStay(data.avg_length_of_stay);
      }
      setDemandHasChanges(false);
    } catch (err) {
      console.error('Error fetching demand data:', err);
      initializeDemandData();
    }
  }, [getAuthToken]);

  const saveDemandData = async () => {
    setLoading(true);
    setError('');
    try {
      const token = await getAuthToken();
      await patch({
        apiName: 'ChulasEmployeeAPI',
        path: '/v1/admin/scheduling/demand',
        options: {
          headers: { Authorization: token },
          body: {
            default_demand: defaultDemandData,
            weekly_overrides: weeklyDemandOverrides,
            online_order_demand: onlineOrderDemand,
            online_order_weekly_overrides: onlineOrderWeeklyOverrides,
            max_capacity: maxCapacity,
            avg_length_of_stay: avgLengthOfStay
          }
        }
      }).response;
      setDemandHasChanges(false);
      setSuccess('Demand plan saved successfully');
    } catch (err) {
      setError(err.message || 'Failed to save demand plan');
    }
    setLoading(false);
  };

  const getEffectiveDemandData = useCallback(() => {
    if (demandViewMode === 'template') {
      return defaultDemandData;
    }
    const weekOverride = weeklyDemandOverrides[demandWeekStart] || {};
    const merged = {};
    DAY_NAMES.forEach(day => {
      merged[day] = { ...(defaultDemandData[day] || {}), ...(weekOverride[day] || {}) };
    });
    return merged;
  }, [demandViewMode, defaultDemandData, weeklyDemandOverrides, demandWeekStart]);

  const getEffectiveOnlineOrderData = useCallback(() => {
    if (demandViewMode === 'template') {
      return onlineOrderDemand;
    }
    const weekOverride = onlineOrderWeeklyOverrides[demandWeekStart] || {};
    const merged = {};
    DAY_NAMES.forEach(day => {
      merged[day] = { ...(onlineOrderDemand[day] || {}), ...(weekOverride[day] || {}) };
    });
    return merged;
  }, [demandViewMode, onlineOrderDemand, onlineOrderWeeklyOverrides, demandWeekStart]);

  const hasWeekOverride = (day, hour) => {
    if (demandViewMode === 'template') return false;
    const weekOverride = weeklyDemandOverrides[demandWeekStart];
    return weekOverride?.[day]?.[hour] !== undefined;
  };

  const hasOnlineOrderWeekOverride = (day, hour) => {
    if (demandViewMode === 'template') return false;
    const weekOverride = onlineOrderWeeklyOverrides[demandWeekStart];
    return weekOverride?.[day]?.[hour] !== undefined;
  };

  const weekHasOverrides = () => {
    const hasInPerson = weeklyDemandOverrides[demandWeekStart] !== undefined &&
           Object.keys(weeklyDemandOverrides[demandWeekStart]).length > 0;
    const hasOnline = onlineOrderWeeklyOverrides[demandWeekStart] !== undefined &&
           Object.keys(onlineOrderWeeklyOverrides[demandWeekStart]).length > 0;
    return hasInPerson || hasOnline;
  };

  const clearWeekOverrides = () => {
    const newOverrides = { ...weeklyDemandOverrides };
    delete newOverrides[demandWeekStart];
    setWeeklyDemandOverrides(newOverrides);
    const newOnlineOverrides = { ...onlineOrderWeeklyOverrides };
    delete newOnlineOverrides[demandWeekStart];
    setOnlineOrderWeeklyOverrides(newOnlineOverrides);
    setDemandHasChanges(true);
    setSuccess(`Cleared overrides for week of ${formatDemandWeekLabel()}`);
  };

  const updateDemandValue = (day, hour, value) => {
    const numValue = Math.max(0, Math.min(parseInt(value) || 0, maxCapacity * 2));

    if (demandViewMode === 'template') {
      setDefaultDemandData(prev => ({
        ...prev,
        [day]: { ...prev[day], [hour]: numValue }
      }));
    } else {
      setWeeklyDemandOverrides(prev => ({
        ...prev,
        [demandWeekStart]: {
          ...(prev[demandWeekStart] || {}),
          [day]: { ...(prev[demandWeekStart]?.[day] || {}), [hour]: numValue }
        }
      }));
    }
    setDemandHasChanges(true);
  };

  const updateOnlineOrderValue = (day, hour, value) => {
    // Online orders have no max limit since they don't consume seating capacity
    const numValue = Math.max(0, parseInt(value) || 0);

    if (demandViewMode === 'template') {
      setOnlineOrderDemand(prev => ({
        ...prev,
        [day]: { ...prev[day], [hour]: numValue }
      }));
    } else {
      setOnlineOrderWeeklyOverrides(prev => ({
        ...prev,
        [demandWeekStart]: {
          ...(prev[demandWeekStart] || {}),
          [day]: { ...(prev[demandWeekStart]?.[day] || {}), [hour]: numValue }
        }
      }));
    }
    setDemandHasChanges(true);
  };

  const getDayTotalOnlineOrders = (day) => {
    const effectiveData = getEffectiveOnlineOrderData();
    const dayData = effectiveData[day] || {};
    return Object.values(dayData).reduce((sum, val) => sum + (val || 0), 0);
  };

  const getWeekTotalOnlineOrders = () => {
    return DAY_NAMES.reduce((sum, day) => sum + getDayTotalOnlineOrders(day), 0);
  };

  const getPeakOnlineOrderHour = (day) => {
    const effectiveData = getEffectiveOnlineOrderData();
    const dayData = effectiveData[day] || {};
    let maxHour = null;
    let maxValue = 0;
    Object.entries(dayData).forEach(([hour, value]) => {
      if (value > maxValue) {
        maxValue = value;
        maxHour = hour;
      }
    });
    return { hour: maxHour, value: maxValue };
  };

  const copyDayToAll = (sourceDay) => {
    const effectiveData = getEffectiveDemandData();
    const sourceData = effectiveData[sourceDay] || {};

    if (demandViewMode === 'template') {
      const newData = { ...defaultDemandData };
      DAY_NAMES.forEach(day => { newData[day] = { ...sourceData }; });
      setDefaultDemandData(newData);
    } else {
      const newOverride = { ...(weeklyDemandOverrides[demandWeekStart] || {}) };
      DAY_NAMES.forEach(day => { newOverride[day] = { ...sourceData }; });
      setWeeklyDemandOverrides(prev => ({ ...prev, [demandWeekStart]: newOverride }));
    }
    setDemandHasChanges(true);
    setSuccess(`Copied ${sourceDay}'s demand to all days`);
  };

  const copyToWeekdays = (sourceDay) => {
    const effectiveData = getEffectiveDemandData();
    const sourceData = effectiveData[sourceDay] || {};
    const weekdays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];

    if (demandViewMode === 'template') {
      const newData = { ...defaultDemandData };
      weekdays.forEach(day => { newData[day] = { ...sourceData }; });
      setDefaultDemandData(newData);
    } else {
      const newOverride = { ...(weeklyDemandOverrides[demandWeekStart] || {}) };
      weekdays.forEach(day => { newOverride[day] = { ...sourceData }; });
      setWeeklyDemandOverrides(prev => ({ ...prev, [demandWeekStart]: newOverride }));
    }
    setDemandHasChanges(true);
    setSuccess(`Copied ${sourceDay}'s demand to weekdays`);
  };

  const copyToWeekends = (sourceDay) => {
    const effectiveData = getEffectiveDemandData();
    const sourceData = effectiveData[sourceDay] || {};
    const weekends = ['saturday', 'sunday'];

    if (demandViewMode === 'template') {
      const newData = { ...defaultDemandData };
      weekends.forEach(day => { newData[day] = { ...sourceData }; });
      setDefaultDemandData(newData);
    } else {
      const newOverride = { ...(weeklyDemandOverrides[demandWeekStart] || {}) };
      weekends.forEach(day => { newOverride[day] = { ...sourceData }; });
      setWeeklyDemandOverrides(prev => ({ ...prev, [demandWeekStart]: newOverride }));
    }
    setDemandHasChanges(true);
    setSuccess(`Copied ${sourceDay}'s demand to weekends`);
  };

  const getDayTotalCovers = (day) => {
    const effectiveData = getEffectiveDemandData();
    const dayData = effectiveData[day] || {};
    return Object.values(dayData).reduce((sum, val) => sum + (val || 0), 0);
  };

  const getWeekTotalCovers = () => {
    return DAY_NAMES.reduce((sum, day) => sum + getDayTotalCovers(day), 0);
  };

  const getPeakHour = (day) => {
    const effectiveData = getEffectiveDemandData();
    const dayData = effectiveData[day] || {};
    let maxHour = null;
    let maxValue = 0;
    Object.entries(dayData).forEach(([hour, value]) => {
      if (value > maxValue) {
        maxValue = value;
        maxHour = hour;
      }
    });
    return { hour: maxHour, value: maxValue };
  };

  const getConcurrentCovers = (day, timeSlot) => {
    const effectiveData = getEffectiveDemandData();
    const dayData = effectiveData[day] || {};
    const slotIndex = DEMAND_HOURS.indexOf(timeSlot);
    if (slotIndex === -1) return 0;

    const slotsToLookBack = Math.ceil(avgLengthOfStay / 15);
    let concurrent = 0;
    for (let i = 0; i < slotsToLookBack; i++) {
      const lookBackIndex = slotIndex - i;
      if (lookBackIndex >= 0) {
        const prevSlot = DEMAND_HOURS[lookBackIndex];
        const arrivals = dayData[prevSlot] || 0;
        const minutesElapsed = i * 15;
        const remainingFraction = Math.max(0, (avgLengthOfStay - minutesElapsed) / avgLengthOfStay);
        concurrent += Math.round(arrivals * remainingFraction);
      }
    }
    return concurrent;
  };

  const getPeakConcurrent = (day) => {
    let maxConcurrent = 0;
    let maxHour = null;
    DEMAND_HOURS.forEach(hour => {
      const concurrent = getConcurrentCovers(day, hour);
      if (concurrent > maxConcurrent) {
        maxConcurrent = concurrent;
        maxHour = hour;
      }
    });
    return { hour: maxHour, value: maxConcurrent };
  };

  const goToPrevDemandWeek = () => {
    const current = new Date(demandWeekStart);
    current.setDate(current.getDate() - 7);
    setDemandWeekStart(current.toISOString().split('T')[0]);
  };

  const goToNextDemandWeek = () => {
    const current = new Date(demandWeekStart);
    current.setDate(current.getDate() + 7);
    setDemandWeekStart(current.toISOString().split('T')[0]);
  };

  const goToCurrentDemandWeek = () => {
    const monday = getMonday(new Date());
    setDemandWeekStart(monday.toISOString().split('T')[0]);
  };

  const formatDemandWeekLabel = () => {
    const start = new Date(demandWeekStart);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  };

  return {
    // State
    defaultDemandData,
    weeklyDemandOverrides,
    onlineOrderDemand,
    onlineOrderWeeklyOverrides,
    maxCapacity,
    setMaxCapacity,
    demandSelectedDay,
    setDemandSelectedDay,
    demandHasChanges,
    setDemandHasChanges,
    avgLengthOfStay,
    setAvgLengthOfStay,
    demandViewMode,
    setDemandViewMode,
    demandWeekStart,
    // Actions
    fetchDemandData,
    saveDemandData,
    getEffectiveDemandData,
    getEffectiveOnlineOrderData,
    hasWeekOverride,
    hasOnlineOrderWeekOverride,
    weekHasOverrides,
    clearWeekOverrides,
    updateDemandValue,
    updateOnlineOrderValue,
    copyDayToAll,
    copyToWeekdays,
    copyToWeekends,
    getDayTotalCovers,
    getWeekTotalCovers,
    getDayTotalOnlineOrders,
    getWeekTotalOnlineOrders,
    getPeakHour,
    getPeakOnlineOrderHour,
    getConcurrentCovers,
    getPeakConcurrent,
    goToPrevDemandWeek,
    goToNextDemandWeek,
    goToCurrentDemandWeek,
    formatDemandWeekLabel
  };
};

export const formatHourLabel = (timeSlot, short = false) => {
  const [hourStr, minStr] = timeSlot.split(':');
  const h = parseInt(hourStr);
  const m = minStr;
  const period = h >= 12 ? 'PM' : 'AM';
  const displayHour = h === 0 ? 12 : (h > 12 ? h - 12 : h);

  if (short) {
    if (m === '00') return `${displayHour}`;
    return `${displayHour}:${m}`;
  }

  if (m === '00') return `${displayHour} ${period}`;
  return `${displayHour}:${m} ${period}`;
};
